import {DropResult} from '@hello-pangea/dnd';
import {useItemStatuesInRedux, useUpdateItemsOrderAndStatuses} from 'hooks';
import {sortBy} from 'lodash';
import {ItemDataResponse, ItemStatus, UpdateItemDataOrderAndStatusesRequest} from 'models/board';
import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {setReloadTheStatusItemsById} from 'store/itemStatues';

// Types
interface ItemDataStateContextType {
    appendBoardItemsDataUnique: (value: ItemDataResponse[]) => void;
    addBoardItemsData: (value: ItemDataResponse) => void;
    removeBoardItemsData: (value: string) => void;
    itemsDataState: ItemDataResponse[] | undefined;
    setItemDatas: (value: ItemDataResponse[]) => void;
    clearBoardItemData: () => void;
    updateItemDataFieldsInState: (value: ItemDataResponse) => void;
    onDragEndKanbanItemHandler: (result: DropResult, statuses: ItemStatus[]) => void;
}

const ItemDataStateContext = createContext<ItemDataStateContextType | undefined>(undefined);

export const ItemDataStateProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
    const {updateItemsOrderAndStatuses, updateItemsOrderAndStatusesLoading, updateItemsOrderAndStatusesResponse} =
        useUpdateItemsOrderAndStatuses();
    const {handleUpdateItemStatusCountAfterUpdate} = useItemStatuesInRedux();
    const dispatch = useDispatch();
    const [sourceColumnId, setSourceColumnId] = useState('');
    const [itemsDataState, setItemsDataState] = useState<ItemDataResponse[]>();

    const updateItemDataFieldsInState = useCallback((value: ItemDataResponse) => {
        setItemsDataState(prev =>
            prev
                ? prev.map(item =>
                      item.id === value.id ? {...item, data: value.data, itemStatusId: value.itemStatusId} : item,
                  )
                : [],
        );
    }, []);

    const onDragEndKanbanItemHandler = useCallback(
        (result: DropResult, statuses: ItemStatus[]) => {
            const {source, destination, draggableId} = result;
            const itemNeedsToUpdate: UpdateItemDataOrderAndStatusesRequest[] = [];
            let draggableItemId: string | null = null;
            if (!destination) return;
            if (destination.droppableId === source.droppableId && destination.index === source.index) return;
            if (!itemsDataState) return;
            const sourceColumn = statuses.find((item: ItemStatus) => item.id === source.droppableId);

            const destinationColumn = statuses.find((item: ItemStatus) => item.id === destination.droppableId);

            // find dropped item's column
            const sourceItemsIds = sortBy(
                itemsDataState.filter(item => item.itemStatusId === sourceColumn?.id),
                ['order'],
            ).map(item => item.id);

            // if - moving items in the same list
            // else - moving items from one list to another
            if (sourceColumn && sourceColumn.id === destinationColumn?.id) {
                // remove the id of dragged item from its original position
                sourceItemsIds.splice(source.index, 1);

                // insert the id of dragged item to the new position
                sourceItemsIds.splice(destination.index, 0, draggableId);

                setItemsDataState(prev => {
                    if (prev) {
                        return prev.map(item => {
                            if (sourceItemsIds.includes(item.id)) {
                                itemNeedsToUpdate.push({
                                    id: item.id,
                                    order: sourceItemsIds.indexOf(item.id) + 1,
                                    itemStatusId: sourceColumn.id,
                                });
                                return {...item, order: sourceItemsIds.indexOf(item.id) + 1};
                            }
                            return item;
                        });
                    }
                    return [];
                });

                // swapItemDatasOrder(sourceColumn.id, newItemIds[destination.index], draggableId);
            } else {
                const destinationItemsIds = sortBy(
                    itemsDataState.filter(item => item.itemStatusId === destinationColumn?.id),
                    ['order'],
                ).map(item => item.id);
                // remove the id of dragged item from its original position
                sourceItemsIds.splice(source.index, 1);

                // insert the id of dragged item to the new position
                destinationItemsIds.splice(destination.index, 0, draggableId);
                // find dragable item in  state and replace source column id with destination column id
                // chamge the order of the desctions column

                draggableItemId = draggableId;
                setItemsDataState(prev => {
                    if (prev) {
                        return prev.map(item => {
                            if (destinationItemsIds.includes(item.id)) {
                                itemNeedsToUpdate.push({
                                    id: item.id,
                                    order: destinationItemsIds.indexOf(item.id) + 1,
                                    itemStatusId: destinationColumn?.id ?? '',
                                });
                                return {
                                    ...item,
                                    order: destinationItemsIds.indexOf(item.id) + 1,
                                    itemStatusId: destinationColumn?.id ?? '',
                                };
                            }
                            return item;
                        });
                    }
                    return [];
                });
            }
            if (sourceColumn?.id) {
                //saving the column id to refecting the source column items data
                setSourceColumnId(sourceColumn?.id);
            }

            updateItemsOrderAndStatuses(itemNeedsToUpdate, draggableItemId);
        },
        [itemsDataState],
    );
    useEffect(() => {
        if (!updateItemsOrderAndStatusesLoading && updateItemsOrderAndStatusesResponse?.data) {
            handleUpdateItemStatusCountAfterUpdate(updateItemsOrderAndStatusesResponse.data);
            dispatch(setReloadTheStatusItemsById(sourceColumnId));
        }
    }, [updateItemsOrderAndStatusesResponse, updateItemsOrderAndStatusesLoading]);

    const appendBoardItemsDataUnique = useCallback((value: ItemDataResponse[]) => {
        setItemsDataState(prev => {
            if (prev) {
                const ids = prev.map(item => item.id);
                const newItems = value.filter(item => !ids.includes(item.id));
                return [...prev, ...newItems];
            }
            return value;
        });
    }, []);

    useEffect(() => {
        if (!updateItemsOrderAndStatusesLoading && updateItemsOrderAndStatusesResponse) {
            dispatch(setReloadTheStatusItemsById(sourceColumnId));
        }
    }, [updateItemsOrderAndStatusesResponse, updateItemsOrderAndStatusesLoading]);

    const clearBoardItemData = useCallback(() => {
        setItemsDataState(undefined);
    }, []);

    const addSingleItem = useCallback((value: ItemDataResponse) => {
        setItemsDataState(prev => (prev ? [value, ...prev] : [value]));
    }, []);
    const setItemDatas = useCallback((value: ItemDataResponse[]) => {
        setItemsDataState(value);
    }, []);

    const removeBoardItemsData = useCallback((value: string) => {
        setItemsDataState(prev => (prev ? prev.filter(item => item.id !== value) : []));
    }, []);

    return (
        <ItemDataStateContext.Provider
            value={{
                setItemDatas,
                appendBoardItemsDataUnique,
                addBoardItemsData: addSingleItem,
                removeBoardItemsData,
                itemsDataState,
                clearBoardItemData,
                updateItemDataFieldsInState,
                onDragEndKanbanItemHandler,
            }}
        >
            {children}
        </ItemDataStateContext.Provider>
    );
};

// Custom Hook to Consume Context
export const useItemDataState = (): ItemDataStateContextType => {
    const context = useContext(ItemDataStateContext);
    if (!context) {
        throw new Error('useItemDataState must be used within an ItemDataStateProvider');
    }
    return context;
};
